import { db } from "@/firebase";
import { Tracking } from "@/tracking";
import { app } from "@/main";
import { doc, getDoc } from "firebase/firestore";
import posthog from "posthog-js";
import { createRouter, createWebHistory } from "vue-router";
import { getCurrentUser } from "vuefire";
import { AUTH_STATUS, sendStatusToParent } from "@/lib/embeddable";
import { getVersionContent } from "@/components/Editor/compareVersions";
import * as Y from "yjs";
import { initializeMethod } from "@/components/Editor/initializeMethod";
import { useSuperAdminClaims } from "@/admin/useSuperAdminClaims";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/welcome",
      component: () => import("../pages/WelcomePage.vue"),
      meta: {
        title: "Briefly, Welcome",
      },
      beforeEnter: async (to, from, next) => {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          // Send status of logged in user to parent frame if this
          // session is embedded in an iframe
          sendStatusToParent(AUTH_STATUS.AUTHENTICATED, {
            userId: currentUser.uid,
            email: currentUser.email || undefined,
            userToken: await currentUser.getIdToken(),
          });
          const userProfile = await getDoc(doc(db, `users/${currentUser.uid}`));
          const workspace = userProfile.data()?.homeWorkspace;
          next({
            path: workspace ? `workspaces/${workspace}` : "/experiments/new",
          });
        } else {
          sendStatusToParent(AUTH_STATUS.UNAUTHENTICATED);
          next();
        }
      },
      children: [
        {
          path: "signup",
          name: "Sign up",
          component: () => import("../pages/SignUp.vue"),
          alias: "/signup",
          meta: {
            title: "Sign up",
          },
        },
        {
          path: "login",
          name: "Log in",
          component: () => import("../pages/LogIn.vue"),
          alias: "/login",
          meta: {
            title: "Log in",
          },
        },
        {
          path: "sso",
          name: "SSO login",
          component: () => import("../pages/SsoLogin.vue"),
          alias: "/sso",
          meta: {
            title: "Sign in with SSO",
          },
        },
      ],
    },
    {
      path: "/admin",
      name: "AdminAutomationDashboard",
      component: () => import("../pages/admin/AdminAutomationDashboard.vue"),
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
        title: "Admin Dashboard",
      },
      redirect: { name: "AdminRunnerOverview" },
      children: [
        {
          path: "overview",
          name: "AdminRunnerOverview",
          component: () => import("../pages/admin/RunnerOverview.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Runner Overview",
          },
        },
        {
          path: "runner",
          name: "AdminRunnerDashboard",
          component: () => import("../pages/admin/RunnerDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Runner Dashboard",
          },
        },
        {
          path: "keys",
          name: "AdminApiKeysDashboard",
          component: () => import("../pages/admin/ApiKeysDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "API Keys",
          },
        },
        {
          path: "superadmins",
          name: "AdminSuperAdminsDashboard",
          component: () => import("../pages/admin/SuperAdminsDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Super Admins",
          },
        },
        {
          path: "impersonation",
          name: "AdminImpersonationDashboard",
          component: () => import("../pages/admin/ImpersonationDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "User Impersonation",
          },
        },
        {
          path: "users",
          name: "AdminUserManagementDashboard",
          component: () => import("../pages/admin/UserManagementDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "User Management",
          },
        },
        {
          path: "templating",
          name: "AdminMethodTemplatingDashboard",
          component: () => import("../pages/admin/MethodTemplatingDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Method Templating",
          },
        },
        {
          path: "migration",
          name: "AdminMigrationDashboard",
          component: () => import("../pages/admin/MigrationDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Migration Dashboard",
          },
        },
        {
          path: "flags",
          name: "AdminFeatureFlagsDashboard",
          component: () =>
            import("../pages/admin/FeatureFlagsDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Feature Flags",
          },
        },
        {
          path: "teams",
          name: "AdminTeamsDashboard",
          component: () => import("../pages/admin/TeamsDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Teams",
          },
        },
        {
          path: "teams/:teamId",
          name: "AdminTeamDetailDashboard",
          component: () => import("../pages/admin/TeamDetailDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Team",
          },
        },
        {
          path: "catalog",
          name: "AdminCatalogDashboard",
          component: () =>
            import("../pages/admin/CatalogDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Equipment Catalog",
          },
        },
        {
          path: "prompts",
          name: "AdminPromptsDashboard",
          component: () => import("../pages/admin/PromptsDashboard.vue"),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            title: "Prompts",
          },
        },
      ],
    },
    // Backwards-compatible redirects from old audit paths
    { path: "/admin/audit", redirect: "/admin" },
    { path: "/admin/audit/runner", redirect: "/admin/runner" },
    { path: "/admin/audit/keys", redirect: "/admin/keys" },
    {
      path: "/admin/check",
      name: "SuperAdminCheck",
      component: () => import("../pages/SuperAdminCheck.vue"),
      meta: {
        requiresAuth: true,
        requiresSuperAdmin: true,
        title: "SuperAdmin Check",
      },
    },
    {
      // Runner device-code approval page. Auth-gated but open to any
      // workspace member — a signed-out user round-trips through login and
      // returns here with the ?code= query intact.
      path: "/activate",
      name: "ActivateRunner",
      component: () => import("../pages/ActivateRunner.vue"),
      meta: {
        requiresAuth: true,
        title: "Activate Runner",
      },
    },
    {
      path: "/",
      name: "Home",
      component: () => null,
      beforeEnter: async (to, from, next) => {
        const currentUser = await getCurrentUser();

        if (!currentUser) {
          next({ path: "/signup" });
        } else {
          const userProfile = await getDoc(doc(db, `users/${currentUser.uid}`));
          const workspace = userProfile.data()?.homeWorkspace;
          next({ path: workspace ? `workspaces/${workspace}` : "/new" });
        }
      },
    },
    {
      path: "/error",
      name: "Error",
      component: () => import("../pages/ErrorPage.vue"),
      meta: {
        title: "Error",
      },
    },
    {
      // Team invite accept page. NO requiresAuth — the page must render
      // for signed-out users (State 2 → "Create account" / "Sign in").
      path: "/invite/team/:teamId/:inviteId",
      name: "AcceptTeamInvite",
      component: () => import("../pages/team/AcceptTeamInvite.vue"),
      meta: { title: "Team Invitation" },
    },
    {
      path: "/",
      component: () => import("../WrapperComponent.vue"),
      children: [
        {
          path: "users/:workspaceId/methods/:methodId",
          name: "nosheet",
          components: {
            default: () => import("../MethodPage.vue"),
            topBarMiddle: () => import("../components/TopBar/MethodTopBar.vue"),
          },
          beforeEnter: async (to, from, next) => {
            const { workspaceId, methodId } = to.params;
            const compareVersionId = to.query["compare-to"];
            let methodDoc;
            try {
              methodDoc = await getDoc(
                doc(db, `workspaces/${workspaceId}/methods/${methodId}`)
              );
            } catch (error) {
              // If the method is private and the user doesn't have access to it
              // forward them to the error page inside the MethodsPage
              next();
              return;
            }
            if (methodDoc.exists()) {
              Tracking.workspace.trackMethodScreen(methodId as string);
              const methodData = methodDoc.data();
              if (methodData.title.length > 0) {
                to.meta.title = methodData.title;
              } else {
                to.meta.title = "Untitled Protocol";
              }
            }

            // If we're loading this protocol in the context of a compare version
            // we need to get the content of the base version and compare it with the
            // currently loaded version.
            if (compareVersionId) {
              try {
                // Fetch binary content via cloud function
                const { binaryDoc, docTitle } = await getVersionContent(
                  workspaceId as string,
                  compareVersionId as string
                );

                to.meta.comparison = {
                  latestDoc: {
                    title: docTitle,
                    url: `/users/${workspaceId}/methods/${compareVersionId}`,
                  },
                  compareDoc: {
                    title: methodDoc.data()?.title,
                    url: `/users/${workspaceId}/methods/${methodId}`,
                  },
                };

                // Load the data into Yjs and extract the root node JSON
                const contentArray = Object.values(binaryDoc) as number[];
                const uint8Array = new Uint8Array(contentArray);
                const ydoc = new Y.Doc();
                Y.applyUpdate(ydoc, uint8Array);

                // Finally we initialize the experiment with a directive
                // to trigger the compare routine. This is checked in
                // Editor.vue onCreate.
                initializeMethod.value = {
                  type: "compareWithMethod",
                  content: ydoc.getXmlFragment("default"),
                };
              } catch (error) {
                console.error("Error fetching version content:", error);
              }
            }
            next();
          },
          children: [
            {
              path: "sheet",
              name: "sheet",
              components: {
                spreadsheet: async () => {
                  const [sf, { registerLicense }] = await Promise.all([
                    import("@syncfusion/ej2-vue-spreadsheet"),
                    import("@syncfusion/ej2-base"),
                  ]);
                  registerLicense(
                    "ORg4AjUWIQA/Gnt2V1hhQlJAfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hSn5UdEViWH1ccXRdTmRU"
                  );
                  app.use(sf.SpreadsheetPlugin);
                  return import(
                    "../components/Spreadsheet/MethodSpreadsheet.vue"
                  );
                },
              },
            },
          ],
        },
        // NOTE: This is a copy of the method routing adjusted for experiments
        // In reality experiments should have different integration with a
        // spreadsheet and a different top bar.
        {
          path: "users/:workspaceId/experiments/:experimentId",
          name: "nosheet-exp",
          components: {
            default: () => import("../ExperimentPage.vue"),
            topBarMiddle: () =>
              import("../components/experiments/ExperimentTopBar.vue"),
          },
          beforeEnter: async (to, from, next) => {
            const { workspaceId, experimentId } = to.params;
            let experimentDoc;
            try {
              experimentDoc = await getDoc(
                doc(db, `workspaces/${workspaceId}/experiments/${experimentId}`)
              );
            } catch (error) {
              // If the experiment is private and the user doesn't have access to it
              // forward them to the error page inside the ExperimentsPage
              next();
              return;
            }
            if (experimentDoc.exists()) {
              Tracking.workspace.trackExperimentScreen(experimentId as string);
              const experimentData = experimentDoc.data();
              if (experimentData.title.length > 0) {
                to.meta.title = experimentData.title;
              } else {
                to.meta.title = "Untitled Labnote";
              }
            }
            next();
          },
          children: [
            {
              path: "sheet/:guid?",
              name: "sheet-exp",
              components: {
                spreadsheet: async () => {
                  const [sf, { registerLicense }] = await Promise.all([
                    import("@syncfusion/ej2-vue-spreadsheet"),
                    import("@syncfusion/ej2-base"),
                  ]);
                  registerLicense(
                    "ORg4AjUWIQA/Gnt2V1hhQlJAfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hSn5UdEViWH1ccXRdTmRU"
                  );
                  app.use(sf.SpreadsheetPlugin);
                  return import(
                    "../components/Spreadsheet/ExperimentSpreadsheet.vue"
                  );
                },
              },
            },
          ],
        },

        {
          path: "test",
          name: "Test editor",
          components: {
            default: () => import("../pages/MethodPageV2.vue"),
            topBarMiddle: () => import("../components/TopBar/MethodTopBar.vue"),
          },
        },
        {
          path: "methods/new",
          name: "New Protocol",
          meta: { requiresAuth: true, title: "New Protocol" },
          component: () => import("../pages/NewProtocolPage.vue"),
          beforeEnter: (to, from, next) => {
            Tracking.workspace.trackNewMethodScreen();
            next();
          },
        },
        {
          path: "experiments/new",
          name: "New Labnote",
          meta: { requiresAuth: true, title: "New Labnote" },
          component: () => import("../pages/NewLabnotePage.vue"),
          alias: "/new",
          beforeEnter: (to, from, next) => {
            Tracking.workspace.trackNewExperimentScreen();
            next();
          },
        },
        {
          path: "workspaces/:workspaceId/duplicate-method/:methodId",
          name: "Duplicate Method",
          meta: { requiresAuth: true, title: "Duplicate Method" },
          component: () => import("../DuplicateMethodPage.vue"),
        },
        {
          path: "workspaces/:workspaceId/duplicate-experiment/:experimentId",
          name: "Duplicate Experiment",
          meta: { requiresAuth: true, title: "Duplicate Experiment" },
          component: () => import("../DuplicateExperimentPage.vue"),
        },
        {
          path: "workspaces/:workspaceId/duplicate-experiment-delta/:experimentId",
          name: "Duplicate Experiment With Changes",
          meta: {
            requiresAuth: true,
            title: "Duplicate Experiment With Changes",
          },
          component: () => import("../DuplicateExperimentWithChangesPage.vue"),
        },
        {
          path: "workspaces/new",
          name: "New Workspace",
          meta: { requiresAuth: true, title: "New Workspace" },
          component: () => import("../NewWorkspacePage.vue"),
          beforeEnter: (to, from, next) => {
            Tracking.workspace.trackNewWorkspaceScreen();
            next();
          },
        },
        {
          path: "workspaces/:workspaceId/config",
          name: "WorkspaceConfig",
          meta: { requiresAuth: true, title: "Workspace Configuration" },
          component: () =>
            import("../pages/workspace/WorkspaceConfigPage.vue"),
        },
        {
          path: "workspaces/:workspaceId",
          name: "Workspace",
          meta: { requiresAuth: true },
          components: {
            default: () => import("../WorkspacePage.vue"),
          },
          beforeEnter: (to, from, next) => {
            Tracking.workspace.trackWorkspaceScreen(
              to.params.workspaceId as string
            );
            to.meta.title = `Workspace - ${to.params.workspaceId}`;
            next();
          },
        },
        {
          path: "trash/:workspaceId",
          name: "Trash",
          meta: { requiresAuth: true, title: "Trash" },
          component: () => import("../TrashPage.vue"),
        },
        {
          path: "/portfolio",
          name: "Portfolio",
          meta: { requiresAuth: false, title: "Portfolio" },
          component: () => import("../pages/profile/Profile.vue"),
          beforeEnter: async (to, from, next) => {
            const currentUser = await getCurrentUser();
            if (currentUser) {
              // Fetch the user's username from their profile
              const user = await getDoc(doc(db, `users/${currentUser.uid}`));
              const username = user.data()?.username;
              if (username) {
                next({ path: `/portfolio/${username}` });
              } else {
                next();
              }
            } else {
              next({ path: "/login" });
            }
          },
        },
        {
          path: "/portfolio/:username",
          name: "PortfolioName",
          meta: { requiresAuth: false },
          component: () => import("../pages/profile/Profile.vue"),
          beforeEnter: async (to, from, next) => {
            const username = to.params.username;
            // Query Firestore for the user with this username
            const profile = await getDoc(doc(db, `profiles/${username}`));
            if (profile.exists()) {
              // Set the page title and always proceed to the profile page
              Tracking.profile.trackProfileView(username as string);
              to.meta.title = `Portfolio - ${username}`;
              next();
            } else {
              to.meta.title = `Portfolio - ${username} not found`;
              next();
            }
          },
        },
        {
          path: "/impersonate",
          name: "impersonate",
          component: () => import("../pages/ImpersonatePage.vue"),
          meta: { requiresAuth: true },
        },
        {
          // Team management page — nested under WrapperComponent so it
          // renders inside the shared TopBar shell. Gated to team admins
          // via in-component guard (we cannot enforce in router meta
          // because the user's teamRole hydration happens after auth
          // resolves).
          path: "/teams/:teamId/manage",
          name: "TeamManagement",
          component: () => import("../pages/team/TeamManagement.vue"),
          meta: { requiresAuth: true, title: "Team Management" },
        },
        {
          path: "/account/workspaces",
          name: "ManageWorkspaces",
          component: () => import("../pages/account/ManageWorkspaces.vue"),
          meta: { requiresAuth: true, title: "Manage Workspaces" },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: (to) => {
        // Backup for the cloud function redirect so people always get to the linked entity
        if (to.path.endsWith("/sharelink")) {
          return to.fullPath.replace("/sharelink", "");
        }
        // Otherwise redirect to error page
        return { name: "Error" };
      },
    },
  ],
});

// Use afterEach guard to change the document title
router.afterEach((to) => {
  const defaultTitle = "Briefly, Experiment Builder";
  const pageTitle =
    typeof to.meta.title === "string" ? to.meta.title : defaultTitle;
  document.title = pageTitle;
});

router.beforeEach(async (to) => {
  // routes with `meta: { requiresAuth: true }` will check for
  // the users, others won't
  if (to.meta.requiresAuth) {
    const currentUser = await getCurrentUser();
    // if the user is not logged in, redirect to the login page
    if (!currentUser) {
      sendStatusToParent(AUTH_STATUS.UNAUTHENTICATED);
      return {
        path: "/login",
        query: {
          // we keep the current path in the query so we can
          // redirect to it after login with
          // `router.push(route.query.redirect || '/')`
          redirect: to.fullPath,
        },
      };
    } else {
      sendStatusToParent(AUTH_STATUS.AUTHENTICATED, {
        userId: currentUser.uid,
        email: currentUser.email || undefined,
        userToken: await currentUser.getIdToken(),
      });
    }
  }
  // SuperAdmin route gating
  if (to.meta.requiresSuperAdmin) {
    console.log("requiresSuperAdmin", to.meta.requiresSuperAdmin);
    const { isSuperAdmin, ready } = useSuperAdminClaims();
    console.log("isSuperAdmin", isSuperAdmin.value);
    if (!ready.value) {
      await ready.wait();
    }
    if (!isSuperAdmin.value) {
      return { path: "/" };
    }
  }
  // TODO: It makes initial loading slower. Consider removing it
  // when we don't have router changing flags.
  await new Promise<void>((resolve) => {
    setTimeout(() => {
      // If it takes too long resolve with default values.
      resolve();
    }, 500);
    posthog.onFeatureFlags(() => {
      resolve();
    });
  });
});
export default router;
